import AvatarLogo from '@assets/svgs/upercut-avatar.svg';
import { ThemeSwitcher } from '@components/ThemeSwitcher/ThemeSwitcher';
import { PROFILE_MENU_ITEMS } from '@constants/DashboardMenu';
import { useAuth } from '@contexts/AuthContext';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Avatar, Box, IconButton, MenuItem, styled, Typography } from '@mui/material';
import Menu from '@mui/material/Menu';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';

interface ProfileMenuProps {
    anchorEl: HTMLElement | null;
    open: boolean;
    onClose: () => void;
}

const ProfileMenuWrapper = styled(Menu)(({ theme }) => ({
    '& .MuiPaper-root': {
        borderRadius: '16px',
        border: `${theme.palette.mode === 'dark' ? '1px' : '0px'} solid ${theme.palette.colorSecondary400.main}`,
        background: theme.palette.mode === 'dark' ? 'rgba(36, 38, 41, 0.20)' : '#FFFFFF',
        boxShadow: theme.palette.mode === 'light' ? '0px 0px 19.7px 0px rgb(37 35 35 / 20%)' : '9px 4px 19.7px -1px rgba(85, 85, 85, 0.20)',
        backdropFilter: 'blur(30px)',
        marginTop: 27.5,
        maxWidth: '325px',
        width: '325px',
    },
    '& .MuiList-root': {
        padding: 0,
    },
    '& .MuiMenuItem-root': {
        padding: '12px',
    },
    '& .profile': {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '19px 24px 23px',
    },
    '& .profile-info': {
        display: 'flex',
        flexDirection: 'column',
        gap: '5px',
    },
    '& .text-content': {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    '& .separator': {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        padding: '16px 13px',
        borderTop: `2px solid ${theme.palette.neutralDarkGray.main}`,
        color: theme.palette.textSecondary.main,
    },
    '& .menu-item': {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        borderRadius: '8px',
        '&:hover': {
            background: theme.palette.backgroundPilsurface.main,
        },
    },
    '& .icon': {
        color: 'rgba(84, 71, 222, 1)',
    },
}));

export const ProfileMenu: React.FC<ProfileMenuProps> = ({ anchorEl, open, onClose }) => {
    const { showSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    
    const handleLogout = () => {
        logout();
        navigate('/auth/login');
        showSnackbar('Successfully logged out', 'success');
    };
    
    // Get display name from user data
    const displayName = user?.fullName || user?.commercialName || user?.name || 'User';
    const displayEmail = user?.email || '';
    const avatarSrc = user?.photo || user?.logo || AvatarLogo;
    return (
        <ProfileMenuWrapper
            anchorEl={anchorEl}
            open={open}
            onClose={onClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}>
            <Box className="profile">
                <Avatar
                    src={avatarSrc}
                    sx={{
                        width: 45,
                        height: 45,
                    }}
                />
                <Box className="profile-info">
                    <Typography
                        variant="labelMediumBold"
                        color="neutralWhite.main"
                        className="text-content">
                        {displayName}
                    </Typography>
                    <Typography variant="labelSmallBold" color="gray.main" className="text-content">
                        {displayEmail}
                    </Typography>
                </Box>
            </Box>
            <Box className="separator">
                <ThemeSwitcher open={true} isProfileMenu={true} />
            </Box>
            <Box className="separator">
                {PROFILE_MENU_ITEMS.map(item => (
                    <MenuItem
                        key={item.id}
                        component={Link}
                        to={item.path || '#'}
                        className="menu-item">
                        <i className={`fi ${item.icon}`} />
                        <Typography variant="labelMediumBold">{item.title}</Typography>
                    </MenuItem>
                ))}
            </Box>
            <Box className="separator">
                <MenuItem
                    onClick={() => handleLogout()}
                    className="menu-item">
                    <i className={`fi fi-br-sign-out`} />
                    <Typography variant="labelMediumBold">Déconnexion</Typography>
                </MenuItem>
            </Box>
        </ProfileMenuWrapper>
    );
};

interface ProfileButtonProps {
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
    isActive: boolean;
}

export const ProfileButton: React.FC<ProfileButtonProps> = ({ onClick, isActive }) => {
    const { user } = useAuth();
    
    // Get display name from user data
    const displayName = user?.fullName || user?.commercialName || user?.name || 'User';
    const avatarSrc = user?.photo || user?.logo || AvatarLogo;
    
    return (
        <IconButton
            sx={{
                background: theme => `${theme.palette.mode === 'dark' ? '#242629' : '#4239b0'}`,
                borderRadius: '32px',
                display: 'flex',
                alignItems: 'center',
                padding: { xs: '5px 8px', sm: '7px 12px' },
                maxHeight: { xs: '38px', sm: '54px' },
                fontSize: '16px',
                fontWeight: 600,
                lineHeight: 'normal',
                fontFamily: theme => theme.typography.fontFamily,
                '&:hover': {
                    backgroundColor: theme => theme.palette.backgroundPilsurface.main,
                },
                backgroundColor: theme => (isActive ? theme.palette.backgroundPilsurface.main : 'none'),
            }}
            onClick={onClick}>
            <Avatar
                src={avatarSrc}
                sx={{
                    width: { xs: 28, sm: 40 },
                    height: { xs: 28, sm: 40 },
                    marginRight: { xs: '6px', sm: '10px' },
                }}
            />
            <Typography sx={{ color: '#ffffff !important', display: { xs: 'none', sm: 'block' } }} fontWeight={700}>{displayName}</Typography>
            <KeyboardArrowDownIcon sx={{ marginLeft: { xs: '4px', sm: '41px' }, color: '#ffffff !important' }} />
        </IconButton>
    );
};
